export interface MeUser {
  id: string;
  email: string | null;
  telegramId: string | null;
  telegramUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  emailVerifiedAt: string | null;
  webCreatedAt: string | null;
  createdAt: string;
}

export interface SubscriptionInfo {
  hasTelegramLink: boolean;
  subscriptionUrl: string | null;
  connectUrl: string | null;
  latestGrant: {
    id: string;
    status: string;
    source: string;
    planCode: string;
    planTitle: string;
    priceRub: string | null;
    durationDays: number;
    targetExpireAt: string | null;
    appliedExpireAt: string | null;
    createdAt: string;
  } | null;
  remote: { status?: string; expireAt?: string | null; subscriptionUrl?: string | null } | null;
  trialAvailable: boolean;
  trialDurationDays: number | null;
  hasActiveRecurring: boolean;
}

export interface DeviceInfo {
  hwid: string;
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  createdAt: string | null;
}

export interface PaymentInfo {
  id: string;
  status: string;
  amountRub: string;
  planCode: string | null;
  planTitle: string | null;
  provider: string;
  createdAt: string;
  paidAt: string | null;
}

export interface ReferralInfo {
  referralCode: string | null;
  webReferralLink: string | null;
  telegramReferralLink: string | null;
  hasTelegramLink: boolean;
  totalReferred: number;
  rewardedCount: number;
  referred: Array<{ id: string; createdAt: string; rewardedAt: string | null; referredLabel: string | null }>;
  incomingReferral: { createdAt: string; rewardedAt: string | null } | null;
}

export interface PlanInfo {
  code: string;
  title: string;
  durationDays: number;
  priceRub: string;
  isRecurringEnabled: boolean;
  oneTimePaymentAllowed: boolean;
}

class ApiError extends Error {
  constructor(public status: number, message: string, public payload?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init?.headers ?? {}) },
    ...init
  });
  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const message =
      (payload && typeof payload === 'object' && 'message' in payload && typeof (payload as { message: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : null) ?? `Ошибка ${res.status}`;
    throw new ApiError(res.status, message, payload);
  }
  return payload as T;
}

export const api = {
  ApiError,
  me: () => request<{ user: MeUser }>('/api/auth/me'),
  register: (email: string, consent: boolean, ref?: string) =>
    request<{ ok: true }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, consent, ...(ref ? { ref } : {}) })
    }),
  login: (email: string, password: string) =>
    request<{ ok: true; user: MeUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    }),
  requestPasswordReset: (email: string) =>
    request<{ ok: true }>('/api/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) }),
  confirmPasswordReset: (token: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    }),
  subscription: () => request<SubscriptionInfo>('/api/me/subscription'),
  activateTrial: () =>
    request<{ ok: true; subscriptionUrl: string | null; durationDays: number }>('/api/me/trial', { method: 'POST' }),
  cancelSubscription: () =>
    request<{ ok: true; canceledRecurring: number; canceledPending: number; hadActiveRecurring: boolean }>(
      '/api/me/cancel-subscription',
      { method: 'POST' }
    ),
  requestTelegramLink: () =>
    request<{ deepLink: string }>('/api/me/telegram-link', { method: 'POST' }),
  unlinkTelegram: () =>
    request<{ ok: true }>('/api/me/telegram-unlink', { method: 'POST' }),
  devices: () => request<{ devices: DeviceInfo[] }>('/api/me/devices'),
  removeDevice: (hwid: string) =>
    request<{ devices: DeviceInfo[] }>(`/api/me/devices/${encodeURIComponent(hwid)}`, { method: 'DELETE' }),
  rotateSubscription: () =>
    request<{ connectUrl: string | null; subscriptionUrl: string | null; removedDevices: number }>(
      '/api/me/rotate-subscription',
      { method: 'POST' }
    ),
  payments: () => request<{ payments: PaymentInfo[] }>('/api/me/payments'),
  plans: () => request<{ plans: PlanInfo[] }>('/api/me/plans'),
  checkout: (planCode: string) =>
    request<{ paymentIntentId: string; checkoutUrl: string }>('/api/me/checkout', {
      method: 'POST',
      body: JSON.stringify({ planCode })
    }),
  referral: () => request<ReferralInfo>('/api/me/referral')
};
